import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { DeckProvider, useChannel, useDeck } from '@/pres/store';
import { Stage } from '@/pres/Stage';
import { SlideRenderer } from '@/pres/SlideRenderer';
import { SLIDES } from '@/pres/slides';
import { Toolbar } from '@/pres/Toolbar';
import { AxisBreak, BREAK_SWITCH_MS, BREAK_TOTAL_MS, VARIANTS, breakEntry } from '@/pres/Transitions';

export default function Deck() {
  const { slug } = useParams();
  return <DeckProvider slug={slug!}><DeckInner /></DeckProvider>;
}

const initial = () => Math.min(SLIDES.length - 1, Math.max(0, (parseInt(window.location.hash.slice(1), 10) || 1) - 1));

function DeckInner() {
  const { slug, loaded, notFound } = useDeck();
  const [i, setI] = useState(initial);
  const [dir, setDir] = useState(1);
  const [breaking, setBreaking] = useState(false);
  const iRef = useRef(i);
  const busy = useRef(false);
  iRef.current = i;

  const show = (c: number, broadcast: boolean) => {
    const from = iRef.current;
    if (c === from || busy.current) return;
    setDir(c > from ? 1 : -1);
    if (from === 6 && c === 7) {
      busy.current = true;
      setBreaking(true);
      setTimeout(() => { breakEntry.v = true; setI(7); }, BREAK_SWITCH_MS);
      setTimeout(() => { setBreaking(false); busy.current = false; breakEntry.v = false; }, BREAK_TOTAL_MS);
    } else setI(c);
    if (broadcast) post({ t: 'goto', i: c });
  };

  const post = useChannel(slug, (m) => {
    if (m.t === 'goto') show(m.i, false);
    if (m.t === 'hello') post({ t: 'goto', i: iRef.current });
  });

  useEffect(() => { window.history.replaceState(null, '', `#${i + 1}`); }, [i]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { post({ t: 'goto', i }); }, []);

  const go = (n: number) => show(Math.min(SLIDES.length - 1, Math.max(0, n)), true);

  const fullscreen = () => (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen());
  const presenter = () => window.open(`/p/${slug}/presenter`, `presenter-${slug}`, 'width=1300,height=820');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName)) return;
      if (['ArrowRight', 'PageDown', ' '].includes(e.key)) { e.preventDefault(); go(iRef.current + 1); }
      else if (['ArrowLeft', 'PageUp'].includes(e.key)) { e.preventDefault(); go(iRef.current - 1); }
      else if (e.key === 'Home') go(0);
      else if (e.key === 'End') go(SLIDES.length - 1);
      else if (e.key.toLowerCase() === 'f') fullscreen();
      else if (e.key.toLowerCase() === 'b') presenter();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (notFound) return (
    <div className="flex h-screen flex-col items-center justify-center gap-4 bg-black font-app text-white" data-testid="deck-not-found">
      <p className="text-2xl">Présentation introuvable.</p><Link to="/" className="text-blue-400 underline">Retour à l'accueil</Link>
    </div>
  );
  if (!loaded) return <div className="flex h-screen items-center justify-center bg-black text-white"><Loader2 className="animate-spin" /></div>;

  const d = SLIDES[i];
  return (
    <div className="relative h-screen w-screen overflow-hidden bg-black" data-testid="deck-view">
      <Stage testId="slide-stage">
        <motion.div className="absolute inset-0" animate={breaking ? { x: [0, -18, 16, -12, 12, -9, 6, -3, 0], rotate: [0, -0.6, 0.5, -0.4, 0.3, 0] } : { x: 0, rotate: 0 }} transition={{ duration: 0.9 }}>
          <AnimatePresence initial={false} custom={dir}>
            <motion.div key={d.id} custom={dir} variants={VARIANTS[d.universe]} initial="enter" animate="center" exit="exit" className="slide-layer absolute inset-0 overflow-hidden" data-testid={`slide-${d.n}`}>
              <SlideRenderer d={d} />
            </motion.div>
          </AnimatePresence>
        </motion.div>
        {breaking && <AxisBreak />}
      </Stage>
      <Toolbar index={i} go={go} fullscreen={fullscreen} presenter={presenter} />
    </div>
  );
}
